import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useBulkUpsertExclusions } from '@/api/domains/excluded-products';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { ExclusionScopePicker } from './ExclusionScopePicker';
import {
  countryItemsFrom,
  methodItemsFrom,
  type CountryOption,
  type ExclusionRow,
  type MethodOption,
} from './lib';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The selected rows the change applies to. */
  rows: ExclusionRow[];
  methodOptions: MethodOption[];
  countryOptions: CountryOption[];
  onApplied: () => void;
};

/**
 * Mass exclusion: the same scope pickers as the single-product dialog, applied to
 * every selected row in one request. Both scopes start fully ticked, so the default
 * action is a blanket exclusion; the backend collapses that complete selection to
 * the stored "all of them" sentinel, which keeps covering methods and countries
 * added to the catalog later.
 */
export function BulkExclusionDialog({
  open,
  onOpenChange,
  rows,
  methodOptions,
  countryOptions,
  onApplied,
}: Props) {
  const { t } = useTranslation('excluded-products');
  const bulk = useBulkUpsertExclusions();

  const [selectedMethods, setSelectedMethods] = useState<Set<string>>(new Set());
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());

  const methodItems = methodItemsFrom(methodOptions);
  const countryItems = countryItemsFrom(countryOptions);

  const methodKeys = useMemo(
    () => methodOptions.map((option) => option.methodKey),
    [methodOptions],
  );
  const countryCodes = useMemo(() => countryOptions.map((option) => option.code), [countryOptions]);

  // A mass action always writes an explicit scope rather than inheriting whatever
  // each row happened to carry, so every open starts from "all" - every box ticked.
  // The catalog lists are deps because they load from their own query: seeding
  // against an empty list would leave every box unticked and Apply disabled.
  useEffect(() => {
    if (open) {
      setSelectedMethods(new Set(methodKeys));
      setSelectedCountries(new Set(countryCodes));
    }
  }, [open, methodKeys, countryCodes]);

  // Nothing ticked would be sent as an empty array, which the backend reads as
  // "all of them" - the exact opposite of an empty checklist.
  const isEmptySelection = selectedMethods.size === 0 || selectedCountries.size === 0;

  const handleApply = () => {
    if (rows.length === 0 || isEmptySelection) return;
    bulk.mutate(
      {
        products: rows.map((row) => ({
          shopify_product_id: row.productId,
          title: row.title,
        })),
        is_active: true,
        excluded_method_keys: [...selectedMethods],
        excluded_countries: [...selectedCountries],
      },
      {
        onSuccess: (result) => {
          toast.success(t('bulk.applied', { count: result.total }));
          onOpenChange(false);
          onApplied();
        },
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[661px] gap-0 p-0">
        <DialogHeader className="space-y-1.5 px-6 pt-6 text-left">
          <DialogTitle className="text-lg font-medium leading-none">
            {t('bulk.dialogTitle', { count: rows.length })}
          </DialogTitle>
          <DialogDescription className="text-sm leading-5">
            {t('bulk.dialogDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-[30px] px-6 py-8">
          <ExclusionScopePicker
            label={t('dialog.excludeServices')}
            allLabel={t('dialog.allServices')}
            items={methodItems}
            selected={selectedMethods}
            onSelectionChange={setSelectedMethods}
          />
          <ExclusionScopePicker
            label={t('dialog.excludeMarkets')}
            allLabel={t('dialog.allMarkets')}
            items={countryItems}
            selected={selectedCountries}
            onSelectionChange={setSelectedCountries}
          />
        </div>

        <DialogFooter className="flex-col items-stretch gap-2 px-6 pb-6 sm:flex-row sm:items-center sm:justify-end sm:space-x-0">
          {isEmptySelection ? (
            <p className="mr-auto text-sm leading-5 text-muted-foreground">
              {t('dialog.selectAtLeastOne')}
            </p>
          ) : null}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={bulk.isPending}>
            {t('dialog.close')}
          </Button>
          <Button
            onClick={handleApply}
            disabled={bulk.isPending || rows.length === 0 || isEmptySelection}
          >
            {t('bulk.apply', { count: rows.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
